import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  userNotificationPrefsApi,
  type NotificationPrefs,
} from "@/api/user-notification-prefs";

const QUERY_KEY = ["notification-prefs"] as const;

/**
 * Reads the current user's notification preferences. Defaults are applied
 * server-side (missing row → toasts enabled), so consumers can treat a
 * `chatToastsEnabled` of `false` as a deliberate opt-out and anything else
 * (including loading) as enabled.
 */
export function useNotificationPrefs() {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => userNotificationPrefsApi.get(),
    staleTime: 60_000,
  });
}

/** Mutation to toggle the chat-toast preference, with optimistic cache update. */
export function useSetChatToastsEnabled() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) => userNotificationPrefsApi.set(enabled),
    onMutate: (enabled) => {
      queryClient.setQueryData<NotificationPrefs>(QUERY_KEY, {
        chatToastsEnabled: enabled,
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}
